import { store } from 'redux/store';
import { useCallback, useEffect, useState } from 'react';

import { Partner, UsufructData } from 'types';
import { CustomRetention } from 'types/Operation';

import { getUsufructData } from 'modules/partners/utils/getUsufructData';
import { getPartnersByFieldsAndQuery } from 'modules/partners/api/getPartnersByFieldsAndQuery';

import { createDividends } from '../utils/createDividends';

type Props = {
  societyId: string;
  societyActualSharesCount: number;
  totalAmount: number;
  page: number;
  rowsPerPage: number;
  retentionPercentage: number;
  customRetentions: CustomRetention[];
};

const fields = 'name sharesCount sharesBurdenCount';

export const useGetDividends = ({
  societyId,
  societyActualSharesCount,
  totalAmount,
  page,
  rowsPerPage,
  retentionPercentage = 0,
  customRetentions,
}: Props) => {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [usufructDataByPartner, setUsufructDataByPartner] = useState<{
    [key: string]: UsufructData[];
  }>({});
  const [dividends, setDividends] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const fetchPartners = useCallback(async () => {
    if (!societyId) return;
    try {
      setIsLoading(true);

      const partners = await store.dispatch(
        getPartnersByFieldsAndQuery(societyId, {
          fields,
          page,
          items: rowsPerPage,
          onlyWithShares: true,
          includeUsufructuaries: true,
        })
      );

      if (!partners?.length) {
        setIsLoading(false);
        return;
      }

      const usufructDataByPartner = await getUsufructData(partners);
      setUsufructDataByPartner(usufructDataByPartner);

      setPartners(partners);

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      setIsError(true);
    }
  }, [page, rowsPerPage, societyId]);

  useEffect(() => {
    const dividends = createDividends({
      partners,
      societyActualSharesCount,
      totalAmount,
      usufructDataByPartner,
      generalRetentionPercentage: retentionPercentage,
      customRetentions,
    });
    setDividends(dividends);
  }, [
    customRetentions,
    partners,
    retentionPercentage,
    societyActualSharesCount,
    totalAmount,
    usufructDataByPartner,
  ]);

  useEffect(() => {
    fetchPartners();
  }, [fetchPartners]);

  return {
    data: { dividends },
    isLoading,
    isError,
  };
};
